import OwnerType from "@/utils/constants/OwnerType.ts";
import PreferencesRequest from "@/utils/constants/PreferencesRequest.ts";
import MetaData from "@/utils/common/MetaData.ts";
import ListPlace from "@/utils/presenters/ListPlace.ts";
import StorageUtils from "@/utils/common/StorageUtils.ts";
import {ColDef, ColGroupDef, GridState, ValueGetterParams} from "ag-grid-community";
import CacheUtil from "@/utils/login/CacheUtil.ts";
import {CriteriaExpressionSerializer} from "@/utils/urlUtils/CriteriaAndQuestionsURLParser.ts";
import ClientCache from "@/utils/login/ClientCache.ts";
import {StringUtils} from "@/utils/common/StringUtils.ts";
import FieldType from "@/utils/constants/FieldType.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import AGGridColumnFactory from "@/utils/UIConstants/AGGridColumns/AGGridColumnFactory.ts";
import {Ref} from "vue";
import AGGridEditableCellLineNumberCellRenderer from "@/components/inputs/AGGridEditableCellLineNumberCellRenderer.vue";
import Translator from "@/utils/common/Translator.ts";
import {QSelectOption, QTreeNode} from "quasar";
import {CustomDTOExpression, WidgetsPresenter} from "@/utils/presenters/interfaces.ts";
import ListPresenter from "@/utils/common/ListPresenter.ts";
import AGGridActionsColumnRenderer from "@/components/inputs/AGGridActionsColumnRenderer.vue";
import SecurityUtils from "@/utils/login/SecurityUtils.ts";
import LoginUtils from "@/utils/login/LoginUtils.ts";
import ApplicableWhen from "@/utils/constants/ApplicableWhen.ts";
import IdsAndConstants from "@/utils/UIConstants/IdsAndConstants.ts";
import StringConstants from "@/utils/constants/StringConstants.ts";
import AbsPresenter from "@/utils/common/AbsPresenter.ts";
import AGGridUtils from "@/utils/UIConstants/AGGridUtils.ts";
import DTOCriteria = namaTypes.DTOCriteria;
import ListLayoutPreferences = namaTypes.ListLayoutPreferences;
import ListScreenColumn = namaTypes.ListScreenColumn;
import ViewName = namaTypes.ViewName;
import UIPreferences = namaTypes.UIPreferences;

export default class ListViewUtils {
  static readonly ASC = 'ASC';
  static readonly DESC = 'DESC';
  static readonly REPEAT_COUNT_COLUMN_ID = '__repeatColumn__';
  static CRITERIA_EXP_ID = 1;

  static sortDirections(): QSelectOption[] {
    return [ListViewUtils.ascSortDir(), ListViewUtils.descSortDir()];
  }

  static ascSortDir(): QSelectOption {
    return {label: Translator.translateId(ListViewUtils.ASC), value: ListViewUtils.ASC};
  }

  static descSortDir(): QSelectOption {
    return {label: Translator.translateId(ListViewUtils.DESC), value: ListViewUtils.DESC};
  }

  static getListViewName(viewNames: ViewName[] | undefined): QSelectOption[] {
    let listViewNames: QSelectOption[] = [];
    if (viewNames && viewNames.length > 0) {
      for (let viewName of viewNames) {
        if (viewName.id && viewName.type == "LIST" && viewName.selectable)
          listViewNames.push({label: Translator.translateTitledIDWithPrefixes(viewName, null), value: viewName.id});
      }
    }
    if (listViewNames.length < 1)
      listViewNames.push({label: StringConstants.DEFAULT, value: Translator.translateId(StringConstants.DEFAULT)});
    return listViewNames;
  }

  static async fetchLayoutPreferences(place: ListPlace): Promise<namaTypes.UIPreferences> {
    let req: Record<any, any> = {
      owner: {
        ownerType: OwnerType.Entity,
        ownerId: place.entityType
      },
      properties: [PreferencesRequest.LIST_LAYOUT, PreferencesRequest.LIST_LAYOUT_PREFERENCES, PreferencesRequest.META_DATA, PreferencesRequest.VIEW_NAMES],
      listLayoutScreenName: place.listViewName
    };
    return await MetaData.fetchPreferences(req, (preferences: namaTypes.UIPreferences) => preferences);
  }

  static configureColumnGrouping(columnDefs: (ColDef | ColGroupDef)[]) {
    let mergedColumns: Record<string, ColGroupDef> = {};
    for (let i = 0; i < columnDefs.length; i++) {
      let def = columnDefs[i];
      if (!def.headerName || !def.headerName.includes('|'))
        continue;
      let commonTitle = def.headerName.substring(0, def.headerName.indexOf('|'));
      let parent = mergedColumns[commonTitle];
      if (!parent) {
        parent = mergedColumns[commonTitle] = {
          headerName: commonTitle,
          children: []
        }
        columnDefs[i] = parent;
      } else {
        columnDefs.splice(i, 1);
        i--;
      }
      def.headerName = def.headerName?.substring(commonTitle.length + 1);
      parent.children.push(def);
    }
  }

  static fetchGridStateFromStorage(presenter: AbsPresenter): GridState {
    let place = presenter.fetchPlace() as ListPlace;
    const value: string | null = localStorage.getItem(StorageUtils.calcListViewGridPath(place));
    let state = (value ? JSON.parse(value) : {}) as GridState;
    if (!state.pagination)
      state.pagination = {};
    if (ObjectChecker.isNotNullOrUndefined(place.currentPage)) {
      state.pagination.page = place.currentPage;
    }
    state.pagination.pageSize = place.pageSize || AGGridUtils.fetchDefaultPageSize(presenter);
    return state;
  }

  static persistGridState(place: ListPlace, state: GridState) {
    localStorage.setItem(StorageUtils.calcListViewGridPath(place), JSON.stringify(state));
  }

  static getListViewCriteria(place: ListPlace): DTOCriteria | undefined {
    if (!place.criteria)
      return CacheUtil.getCachedCriteria(place.entityType!, place.listViewName);
    else
      return new CriteriaExpressionSerializer().criteriaFromURL(place.criteria);
  }

  static updateCache(request: namaTypes.AGGridListRequest, layoutId?: string) {
    const type = request.entityType;
    if (!type)
      return;
    let uiPreferences: UIPreferences | null = ClientCache.uiPreferencesCache[type];
    if (!uiPreferences) {
      uiPreferences = {}
      ClientCache.uiPreferencesCache[type] = {};
    }
    const listLayoutPreferences: ListLayoutPreferences = {};
    listLayoutPreferences.questionFields = request.questionFields;
    listLayoutPreferences.criteria = request.criteria;
    listLayoutPreferences.sortBy = request.orderBy;
    listLayoutPreferences.layoutId = layoutId;
    listLayoutPreferences.displayedColumns = request.displayedColumns;
    uiPreferences.listLayoutPreferences = listLayoutPreferences;
  }

  static fetchCurrentSortValueList(sortOptions: Record<string, string>[], sortValue: string) {
    return StringUtils.csvLineToList(sortValue).map(l => sortOptions.find(x => x.value === l)).filter(l => !!(l));
  }

  static childrenOfCol(l: ColDef | ColGroupDef): ColDef[] | ColGroupDef[] {
    if (!(l as ColGroupDef).children)
      return [l];
    return (l as ColGroupDef).children.flatMap(child => this.childrenOfCol(child));
  }

  static createDisplayColumns(displayColumns: namaTypes.ListScreenColumn[], entityType: string, extraOptions: ExtraDisplayColumnMethodOptions) {
    let columnDefs: (ColDef | ColGroupDef)[] = [];
    if (extraOptions.editableLineNumber)
      columnDefs.push(this.createEditableLineNumberColumn());
    const TEXT_FIELD = {fieldType: FieldType.Text};

    const preferredColumnIds: string[] = extraOptions?.listLayoutPreferences?.displayedColumns ?? []
    const preferredOrderByColumnId = new Map<string, number>()
    preferredColumnIds.forEach((id, i) => {
      if (!preferredOrderByColumnId.has(id))
        preferredOrderByColumnId.set(id, i)
    })
    const orderedDisplayColumns: ListScreenColumn[] = [...displayColumns].sort((a, b) => {
      const ai = preferredOrderByColumnId.get(a.id ?? '') ?? Number.MAX_SAFE_INTEGER
      const bi = preferredOrderByColumnId.get(b.id ?? '') ?? Number.MAX_SAFE_INTEGER
      return ai - bi
    })

    for (let displayColumn of orderedDisplayColumns) {
      let fieldId = displayColumn.id!;
      if (!SecurityUtils.userCanViewField(LoginUtils.loginInfo?.userInfo, entityType, fieldId, ApplicableWhen.All))
        continue;
      if (!displayColumn.metaData)
        displayColumn.metaData = ObjectChecker.getFirstNotNullObj((extraOptions.metaData ? MetaData.findFieldMetadata(fieldId, extraOptions.metaData)
          : TEXT_FIELD), TEXT_FIELD);
      let colDef: ColDef = AGGridColumnFactory.createColDefForType(displayColumn, entityType!, extraOptions);
      if (fieldId.includes('.'))
        colDef.valueGetter = params => params.data[fieldId];
      if (extraOptions.listLayoutPreferences && preferredColumnIds.length)
        colDef.hide = !preferredColumnIds.includes(fieldId);
      else if (!displayColumn.displayed)
        colDef.hide = true;
      columnDefs.push(colDef);
    }
    ListViewUtils.configureColumnGrouping(columnDefs);
    if (extraOptions.presenter instanceof ListPresenter && !!extraOptions.presenter.layout?.actionsColumn?.length)
      columnDefs.push(this.createActionsColumn(extraOptions.presenter))
    return columnDefs;
  }

  static createEditableLineNumberColumn(): ColDef {
    let column: ColDef = {};
    column.field = this.REPEAT_COUNT_COLUMN_ID;
    column.colId = this.REPEAT_COUNT_COLUMN_ID;
    column.headerName = '#';
    column.sortable = false;
    column.cellRenderer = AGGridEditableCellLineNumberCellRenderer;
    column.valueGetter = (params: ValueGetterParams) => params.data[this.REPEAT_COUNT_COLUMN_ID];
    column.cellClass = "nama-none-acc";
    column.maxWidth = 100;
    column.resizable = false;
    return column;
  }

  static filterSortOption(value: string | null | undefined, target: Ref<Record<string, string>[]>, originalOptions: Record<string, string>[]) {
    if (!value) {
      target.value = originalOptions;
      return;
    }
    target.value = originalOptions.filter(e =>
      (e.searchData.includes(value)
        || e.searchData.includes(StringUtils.normalizeArabic(value))
        || e.searchData.includes(StringUtils.normalizeArabic(StringUtils.englishToArabic(value)))
        || e.searchData.includes(StringUtils.arabicToEnglish(value).toLowerCase())))
      .sort((a, b) => a.id === b.id ? 0 : a.searchData!.length - b.searchData!.length);
  }

  static isFilterEmpty(criteriaTree: QTreeNode<CustomDTOExpression>[]) {
    return !(criteriaTree && criteriaTree.length && criteriaTree[0].children && criteriaTree[0].children.length);
  }

  private static createActionsColumn(presenter: ListPresenter): ColDef {
    return {
      editable: false,
      colId: IdsAndConstants.ACTIONS_COLUMN,
      cellRenderer: AGGridActionsColumnRenderer,
      cellRendererParams: {
        actions: presenter.layout?.actionsColumn,
        presenter: () => presenter,
      },
      cellClass: "tw-flex tw-items-center nama-full-width-cell",
      autoHeight: true,
    };
  }
}

export type ExtraDisplayColumnMethodOptions =
  {
    metaData?: namaTypes.NaMaMetaData | undefined
    listLayoutPreferences?: ListLayoutPreferences | undefined
    editableLineNumber?: boolean,
    forSearcher?: boolean,
    isReportListView?: boolean,
    appendRowNumberToSelectCheckbox?: boolean,
    layout?: namaTypes.ListScreenLayout & namaTypes.SearcherScreenLayout,
    presenter?: WidgetsPresenter
  }
